// Base API Configuration for MUU Platform
import { API_CONFIG, JWT_CONFIG } from '../config/api';

// Re-export API_CONFIG for backward compatibility
export { API_CONFIG };

// Use enhanced config
const config = API_CONFIG;

// HTTP Client wrapper
export class ApiClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;
  private tokenService: any = null;

  constructor(apiConfig: typeof API_CONFIG) {
    this.baseURL = apiConfig.baseURL;
    this.defaultHeaders = apiConfig.headers;
    
    // Lazy load token service to avoid circular dependency
    this.initializeTokenService();
  }

  private async initializeTokenService() {
    try {
      const { tokenService } = await import('./auth/tokenService');
      this.tokenService = tokenService;
    } catch (error) {
      console.warn('Token service not available:', error);
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit & { skipAuth?: boolean; retryOnTokenRefresh?: boolean; isAuthEndpoint?: boolean; responseType?: 'json' | 'blob' | 'text' | 'arrayBuffer' } = {}
  ): Promise<T> {
    // Auth endpoints don't use /api/v2 prefix
    const baseUrl = options.isAuthEndpoint 
      ? this.baseURL.replace('/api/v2', '') 
      : this.baseURL;
    const url = `${baseUrl}${endpoint}`;
    
    // Prepare headers
    const headers: Record<string, string> = {
      ...this.defaultHeaders,
    };
    
    // Add options headers if provided
    if (options.headers) {
      if (options.headers instanceof Headers) {
        // Convert Headers object to plain object
        options.headers.forEach((value, key) => {
          headers[key] = value;
        });
      } else if (typeof options.headers === 'object' && !Array.isArray(options.headers)) {
        // Spread plain object headers
        Object.assign(headers, options.headers);
      }
    }

    // Add authentication token if available and not skipped
    if (!options.skipAuth) {
      let tokenToUse = null;
      
      // Try to get JWT token first
      if (this.tokenService) {
        const accessToken = this.tokenService.getAccessToken();
        if (accessToken) {
    
          // Check if token needs refresh
          if (this.tokenService.needsRefresh(accessToken)) {

            try {
              await this.tokenService.refreshTokens();
              const newToken = this.tokenService.getAccessToken();
              if (newToken) {
                tokenToUse = newToken;
        
              }
            } catch (error) {
              console.warn('Token refresh failed:', error);
              // Continue with existing token
              tokenToUse = accessToken;
      
            }
          } else {
            tokenToUse = accessToken;
      
          }
        } else {
    
        }
      } else {
  
      }
      
      // Nunca utilize tokens de teste vindos de env no frontend
      
      // Set authorization header
      if (tokenToUse) {
        headers['Authorization'] = `${JWT_CONFIG.tokenPrefix} ${tokenToUse}`;
    
      } else {
        console.warn('ApiClient: No token available for request to', endpoint);
      }
    }

    // If sending FormData, ensure Content-Type header is removed so browser sets boundary
    const isFormDataBody = typeof FormData !== 'undefined' && options.body instanceof FormData;
    if (isFormDataBody && headers['Content-Type']) {
      delete headers['Content-Type'];
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);
      
      // Handle 401 Unauthorized - token might be expired
      if (response.status === 401 && !options.skipAuth && this.tokenService) {
        // Try to refresh token and retry request
        if (options.retryOnTokenRefresh !== false) {
          try {
            const refreshSuccess = await this.tokenService.refreshTokens();
            if (refreshSuccess) {
              // Retry request with new token
              return this.request(endpoint, { ...options, retryOnTokenRefresh: false });
            }
          } catch (refreshError) {
            console.error('Token refresh failed on 401:', refreshError);
          }
        }
        
        throw new ApiError('Authentication failed', 401, { endpoint });
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const serverMessage =
          errorData?.data?.errors?.[0]?.description ||
          errorData?.errors?.[0]?.description ||
          errorData?.data?.error ||
          errorData?.data?.message ||
          errorData?.error ||
          errorData?.message ||
          errorData?.Message ||
          '';
        throw new ApiError(
          serverMessage || `HTTP error! status: ${response.status}`,
          response.status,
          errorData
        );
      }
      
      // Handle different response types (default to JSON)
      const responseType = options.responseType || 'json';
      if (responseType === 'blob') {
        const data = await response.blob();
        return data as unknown as T;
      }
      if (responseType === 'text') {
        const data = await response.text();
        return data as unknown as T;
      }
      if (responseType === 'arrayBuffer') {
        const data = await response.arrayBuffer();
        return data as unknown as T;
      }
      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      console.error('API request failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Network error';
      throw new ApiError(
        errorMessage,
        0,
        { endpoint, originalError: error }
      );
    }
  }

  async get<T>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
      headers,
    });
  }

  // New: GET returning Blob (binary) while preserving auth headers
  async getBlob(endpoint: string, headers?: Record<string, string>): Promise<Blob> {
    return this.request<Blob>(endpoint, {
      method: 'GET',
      headers,
      responseType: 'blob',
    });
  }

  async post<T>(
    endpoint: string,
    body?: any,
    optionsOrHeaders?: Record<string, string> | { skipAuth?: boolean; headers?: Record<string, string> }
  ): Promise<T> {
    // Handle both old signature (headers) and new signature (options)
    let headers: Record<string, string> | undefined;
    let skipAuth: boolean | undefined;
    
    if (optionsOrHeaders) {
      if ('skipAuth' in optionsOrHeaders || 'headers' in optionsOrHeaders) {
        // New signature with options
        const options = optionsOrHeaders as { skipAuth?: boolean; headers?: Record<string, string> };
        headers = options.headers;
        skipAuth = options.skipAuth;
      } else {
        // Old signature with headers only
        headers = optionsOrHeaders as Record<string, string>;
      }
    }
    
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      headers,
      skipAuth,
    });
  }

  // New: POST with FormData body (no JSON serialization)
  async postFormData<T>(
    endpoint: string,
    formData: FormData,
    options?: { skipAuth?: boolean; headers?: Record<string, string> }
  ): Promise<T> {
    const headers = options?.headers;
    const skipAuth = options?.skipAuth;

    return this.request<T>(endpoint, {
      method: 'POST',
      body: formData,
      headers,
      skipAuth,
    });
  }

  async put<T>(
    endpoint: string,
    body?: any,
    headers?: Record<string, string>
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
      headers,
    });
  }

  async delete<T>(
    endpoint: string,
    headers?: Record<string, string>
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      headers,
    });
  }

  // Auth-specific methods that don't use /api/v2 prefix
  async authPost<T>(
    endpoint: string,
    body?: any,
    headers?: Record<string, string>
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      headers,
      isAuthEndpoint: false, // Changed to false to keep /api/v2 prefix
      skipAuth: true,
    });
  }

  async authGet<T>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
      headers,
      isAuthEndpoint: true,
      skipAuth: true,
    });
  }

  // Auth GET returning Blob (for binary from auth endpoints)
  async authGetBlob(endpoint: string, headers?: Record<string, string>): Promise<Blob> {
    return this.request<Blob>(endpoint, {
      method: 'GET',
      headers,
      isAuthEndpoint: true,
      skipAuth: true,
      responseType: 'blob',
    });
  }
}

// Create singleton instance with enhanced config
export const apiClient = new ApiClient(config);

// Error handling utilities
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Helper function to handle API responses
export function handleApiResponse<T>(response: any): T {
  if (response.status !== 'success') {
    throw new ApiError(
      response.message || 'API request failed',
      response.code,
      response
    );
  }
  return response.data;
}
